import React from "react";

import { Box } from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import { useMutation, useQuery } from "@tanstack/react-query";

import ModernModal from "../../../common/ModernModal";
import {
  ModernSubmitButton,
  ModernFormAlert,
  ModernRadioField,
} from "../../../common/ModernForm";
import { RoleName } from "../../../common/enums/RoleName";
import { userManagementService } from "../../services";
import {
  OrganizationUser,
  UpdateUsersRolePayload,
} from "../../services/user-management.service";
import { useUserState } from "../../../../store";
import { usersService } from "../../../common/Headerbar/services";

const roleDescriptions: Record<RoleName, string> = {
  [RoleName.VIEWER]: "Can view content and access basic features",
  [RoleName.EDITOR]: "Can create and modify content",
  [RoleName.ADMIN]: "Full access to all features and user management",
};

const roleOptions = Object.values(RoleName).map((role) => ({
  value: role,
  label: role,
  description: roleDescriptions[role],
}));

interface Props {
  handleClose: () => void;
  handleRoleUpdate: () => void;
  open: boolean;
  user: OrganizationUser;
}

function getAuthenticatedUser() {
  const { data: authenticatedUser } = useQuery({
    queryKey: ["authenticated-user"],
    queryFn: () => usersService.fetchAuthenticardUser(),
  });

  return authenticatedUser;
}

export default function ManageRolesDialog({
  handleClose,
  handleRoleUpdate,
  open,
  user,
}: Props) {
  const { updateUser } = useUserState();

  const { mutateAsync, isError, error, isPending, isSuccess } = useMutation({
    mutationKey: ["add-user-to-organization"],
    mutationFn: (values: UpdateUsersRolePayload) =>
      userManagementService.updateUsersRole(values),
  });

  const handleUpdateUserRole = async (values: { role: RoleName }) => {
    await mutateAsync({ id: user.id, roles: [values.role] });
    handleRoleUpdate();
    const authenticatedUser = await getAuthenticatedUser();
    if (authenticatedUser) {
      updateUser(authenticatedUser);
    }
  };

  // eslint-disable-next-line consistent-return
  React.useEffect(() => {
    if (isSuccess) {
      const timer = setTimeout(handleClose, 2000);
      return () => clearTimeout(timer);
    }
  }, [isSuccess, handleClose]);

  return (
    <ModernModal
      open={open}
      onClose={handleClose}
      title="Modify User Role"
      maxWidth="sm"
    >
      <Formik
        initialValues={{ role: user.roles[0] }} // Take the first role as initial
        validationSchema={Yup.object({
          role: Yup.string().required("Role is required"),
        })}
        onSubmit={handleUpdateUserRole}
      >
        {(formik) => (
          <form onSubmit={formik.handleSubmit}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {isError && (
                <ModernFormAlert severity="error">
                  {error?.message ||
                    "Failed to update user role. Please try again."}
                </ModernFormAlert>
              )}

              {isSuccess && (
                <ModernFormAlert severity="success">
                  Role updated successfully!
                </ModernFormAlert>
              )}

              <ModernRadioField
                label="Select User Role"
                name="role"
                value={formik.values.role}
                onChange={formik.handleChange}
                options={roleOptions}
                error={!!formik.errors.role && formik.touched.role}
                helperText={formik.touched.role ? formik.errors.role : ""}
              />

              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  justifyContent: "flex-end",
                  mt: 2,
                }}
              >
                <ModernSubmitButton
                  label="Cancel"
                  type="button"
                  variant="outlined"
                  onClick={handleClose}
                  disabled={isPending || isSuccess}
                />
                <ModernSubmitButton
                  label="Update Role"
                  loading={isPending}
                  disabled={isPending || !formik.isValid || isSuccess}
                />
              </Box>
            </Box>
          </form>
        )}
      </Formik>
    </ModernModal>
  );
}
